import { Directory, File, Paths } from "expo-file-system";
import { Platform } from "react-native";

const UPLOAD_STAGING_DIR = "montu-upload-staging";
const MAX_STAGING_ATTEMPTS = 5;

function isAlreadyExistsError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  // Matches both the JS layer ("Destination already exists") and the native
  // cause ("java.nio.file.FileAlreadyExistsException").
  return /already\s*exists/i.test(message);
}

/**
 * On Android the URI returned by ImagePicker / DocumentPicker is often a
 * `content://` URI that can't be read with random-access offsets, and the
 * picker's own copy lands in the cache directory, which the system is free to
 * reclaim at any moment. Copying the source into the app's documents directory
 * yields a plain file we can chunk freely and that survives cache eviction.
 * iOS / web work fine with the original URI.
 */
export async function stageOnDisk(uri: string): Promise<File> {
  const source = new File(uri);
  if (Platform.OS !== "android") return source;
  const stagingDir = new Directory(Paths.document, UPLOAD_STAGING_DIR);
  stagingDir.create({ intermediates: true, idempotent: true });
  const extension = source.extension || "";

  // `File.copy` rejects with "Destination already exists"
  // (java.nio.file.FileAlreadyExistsException) when anything is already at the
  // path — a leftover from a crashed upload, a quick re-stage of the same pick,
  // or a same-millisecond name collision on devices with a coarse clock. The
  // pre-copy `exists`/`delete` guard alone is not enough: the file can reappear
  // between the check and the copy. Each attempt uses a brand-new unique name
  // and, on an "already exists" collision, deletes the partial copy and retries
  // with a fresh name so the copy cannot be defeated by a stale file.
  let lastError: unknown;
  for (let attempt = 0; attempt < MAX_STAGING_ATTEMPTS; attempt++) {
    const fileName = `upload-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}-${attempt}${extension}`;
    const destination = new File(stagingDir, fileName);
    try {
      if (destination.exists) destination.delete();
      // `copy` is async since SDK 56, and since SDK 58 Android runs it on an IO
      // thread: un-awaited, the upload starts reading a missing/partial file.
      await source.copy(destination);
      return destination;
    } catch (e) {
      lastError = e;
      // Clean up any partial copy before retrying with a new name.
      try {
        if (destination.exists) destination.delete();
      } catch {
        // Best-effort cleanup; ignore.
      }
      // Only retry on a name collision. Anything else (e.g. out of space, an
      // unreadable source) won't be fixed by a new name — fail fast.
      if (!isAlreadyExistsError(e)) throw e;
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error("No pudimos preparar el archivo para la carga.");
}

export function removeStagedFile(file: File | null) {
  if (!file || Platform.OS !== "android") return;
  try {
    if (file.exists) file.delete();
  } catch {
    // Best-effort cleanup; don't shadow upload result if deletion fails.
  }
}

export type StagedUpload = {
  /** The URI to upload from — the staged copy, or the original when unstaged. */
  uri: string;
  /** Removes the staged copy. Safe to call more than once, and on any platform. */
  cleanup: () => void;
};

/**
 * URI-in / URI-out wrapper around {@link stageOnDisk} for callers that hand a
 * plain URI to an uploader (e.g. Apollo multipart via `ReactNativeFile`) rather
 * than reading chunks themselves.
 *
 * Staging is best-effort: if the copy fails we fall back to the original URI so
 * a working upload is never traded for a staging edge case. The platform guard
 * comes first so web/iOS never touch `Paths.document`.
 */
export async function stageUploadUri(uri: string): Promise<StagedUpload> {
  const noop = { uri, cleanup: () => {} };
  if (Platform.OS !== "android") return noop;
  try {
    const staged = await stageOnDisk(uri);
    return { uri: staged.uri, cleanup: () => removeStagedFile(staged) };
  } catch {
    return noop;
  }
}
